/**
 * New-level wizard logic — the Content⇄Levels bidirectional contract in action
 * (design §10.1). Given an optional designer brief ("Make a fire level!"), the AI
 * pitches ONE level concept: which EXISTING Content mechanics it uses and which NEW
 * mechanics it proposes. Accepted new mechanics are written into the Content design
 * doc (as modular items) BEFORE generation, so the course generates against them and
 * the design docs stay the source of truth.
 */
import { z } from 'zod';
import type { DesignProject } from '@volley/crucible-ipc-types';
import type { CrucibleClient } from '@volley/crucible-client';
import { aiChat, readSection, writeSection } from './anvil-api';
import { parseAiJson } from './schemas';
import { parseItems, serializeItems, type DesignItem } from './lib/section-items';
import { extractTags } from './lib/tags';

export const LevelPitchSchema = z.object({
  /** Level name, e.g. "Fire Gauntlet". */
  name: z.string().min(1),
  /** One-sentence pitch of the course. */
  summary: z.string().min(1),
  /** Proposed level kind (design §17 palette) — the wizard shows + lets you override it. */
  kind: z.enum(['spatial-2d', 'topdown-2d', 'data-table', 'board-grid']).optional(),
  /** One line on why that kind fits (shown in the plan box). */
  kindReason: z.string().optional(),
  /** 1-4 lowercase role tags stamped onto the entry (ladder, boss, fire…). */
  tags: z.array(z.string()).max(6).optional(),
  mechanics: z
    .array(
      z.object({
        name: z.string().min(1),
        status: z.enum(['existing', 'new']),
        /** One line — becomes the Content item's slug for new mechanics. */
        description: z.string().min(1),
        /** Short details markdown — becomes the Content item's body (new only). */
        details: z.string().optional(),
      }),
    )
    .min(1),
});
export type LevelPitch = z.infer<typeof LevelPitchSchema>;

function contentSection(project: DesignProject): { filename: string } | undefined {
  return project.sections.find((s) => s.id.toLowerCase().includes('content'));
}

export interface PaletteItem {
  name: string;
  tags: string[];
}

/** Flattened Content items (top-level + children) with their tags — the existing palette. */
export async function readContentPalette(
  client: CrucibleClient,
  gameId: string,
  project: DesignProject,
): Promise<PaletteItem[]> {
  const cs = contentSection(project);
  if (!cs) return [];
  try {
    const md = await readSection(client, gameId, cs.filename);
    const parsed = parseItems(md);
    const items: PaletteItem[] = [];
    for (const item of parsed.items) {
      items.push({ name: item.name, tags: extractTags(item.body).tags });
      for (const child of item.children) {
        items.push({ name: child.name, tags: extractTags(child.body).tags });
      }
    }
    return items;
  } catch {
    return [];
  }
}

/** One palette line for a prompt: `Rolling Log [hazard, timing]`. */
export function paletteLine(p: PaletteItem): string {
  return p.tags.length > 0 ? `${p.name} [${p.tags.join(', ')}]` : p.name;
}

async function designDump(
  client: CrucibleClient,
  gameId: string,
  project: DesignProject,
): Promise<string> {
  const ordered = [...project.sections].sort((a, b) => a.order - b.order);
  const parts: string[] = [];
  for (const s of ordered) {
    try {
      parts.push(`\n## ${s.title}\n${await readSection(client, gameId, s.filename)}`);
    } catch {
      /* skip unreadable */
    }
  }
  return parts.join('\n');
}

/** Ask the co-designer to pitch one level concept for the brief. */
export async function pitchLevel(
  client: CrucibleClient,
  args: {
    gameId: string;
    project: DesignProject;
    brief: string;
    /** The game's feel archetype — names the genre in the pitch prompt. */
    archetype?: string;
  },
): Promise<LevelPitch> {
  const { gameId, project, brief, archetype } = args;
  const palette = await readContentPalette(client, gameId, project);
  const dump = await designDump(client, gameId, project);
  const genre =
    archetype === 'topdown-2d'
      ? 'a top-down 2D action-adventure (rooms, corridors, keys and locked doors)'
      : archetype === 'data-table'
        ? 'a property-driven game — a "level" is a typed data set (starting board, loot table, grab-bag source), not a spatial layout'
        : 'a 2D auto-runner';
  const defaultKind =
    archetype === 'topdown-2d'
      ? 'topdown-2d'
      : archetype === 'data-table'
        ? 'data-table'
        : 'spatial-2d';

  const system = `You are the game's co-designer pitching ONE level concept for ${genre}.
Output ONLY JSON (no prose, no markdown fences):
{
  "name": "<short evocative level name>",
  "summary": "<one sentence: what makes this course distinct>",
  "kind": "spatial-2d" | "topdown-2d" | "data-table" | "board-grid",
  "kindReason": "<one line: why this kind fits this level>",
  "tags": ["<1-4 lowercase role tags (e.g. fire, boss, level-3) — cross-refs + build routing>"],
  "mechanics": [
    { "name": "<mechanic>", "status": "existing" | "new",
      "description": "<one line — what it does in play>",
      "details": "<new only: 2-4 short lines of design-doc detail (tunables welcome)>" }
  ]
}

KINDS (pick per level — a game can mix them): spatial-2d = a side-scroll 2D track of
vertices/segments; topdown-2d = a top-down floor plan of rooms/corridors/keys; data-table =
a typed data set (loot table, config rows); board-grid = a paintable W×H tile board
(match-3 starting boards, tactics maps). This game's default is "${defaultKind}" —
prefer it unless the request clearly describes another shape.

RULES:
- "existing" ONLY if the name matches this Content palette (else mark it "new"):
  ${palette.length > 0 ? palette.map((p) => `• ${paletteLine(p)}`).join('\n  ') : '(empty — everything you use will be new)'}
- Prefer REUSING existing mechanics; add new ones only where the request genuinely needs them
  (e.g. "fire level" → reuse Fence/Pit, add "Fireball Geyser" that erupts from pits).
- 2-5 mechanics total. New mechanics must be concrete and buildable, named like Content items.
- In "details", reference other design entries as @Mentions (name with spaces removed, e.g.
  @RollingLog) and themes as #tags — these render as clickable links in the design docs.`;

  const request =
    brief.trim() ||
    '(no specific request — pitch the course this game most needs next, using your best judgment; prefer existing mechanics)';
  const user = `Game design:\n${dump}\n\nDesigner's request: ${request}\n\nPitch the level now. Output ONLY the JSON.`;
  const resp = await aiChat(client, {
    gameId,
    system,
    messages: [{ role: 'user', content: user }],
    maxTokens: 4096,
  });
  return parseAiJson(resp.text, LevelPitchSchema);
}

export const CampaignPitchSchema = z.object({
  name: z.string().min(1),
  summary: z.string().min(1),
  levels: z
    .array(
      z.object({
        name: z.string().min(1),
        summary: z.string().min(1),
        /** Per-level generation brief (theme + what escalates here). */
        brief: z.string().min(1),
      }),
    )
    .min(2)
    .max(10),
});
export type CampaignPitch = z.infer<typeof CampaignPitchSchema>;

/** Pitch a whole N-level arc (names/themes/escalation). Reuses the existing palette only. */
export async function pitchCampaign(
  client: CrucibleClient,
  args: {
    gameId: string;
    project: DesignProject;
    brief: string;
    count: number;
    archetype?: string;
  },
): Promise<CampaignPitch> {
  const { gameId, project, brief, count, archetype } = args;
  const palette = await readContentPalette(client, gameId, project);
  const dump = await designDump(client, gameId, project);
  const genre =
    archetype === 'topdown-2d'
      ? 'a top-down 2D action-adventure (rooms, corridors, keys and locked doors)'
      : archetype === 'data-table'
        ? 'a property-driven game — each "level" is a typed data set (starting board, loot table), not a spatial layout'
        : 'a 2D auto-runner';
  const system = `You are the game's co-designer pitching a CAMPAIGN ARC of exactly ${count}
levels for ${genre}. Output ONLY JSON (no prose, no markdown):
{ "name": "<campaign name>", "summary": "<one sentence>",
  "levels": [ { "name": "<level name>", "summary": "<one line>",
                "brief": "<a concrete generation brief: theme, which mechanics it features, what escalates>" } ] }
RULES:
- Exactly ${count} levels, escalating difficulty from tutorial-friendly to finale.
- Use ONLY existing Content mechanics: ${palette.length > 0 ? palette.map(paletteLine).join(', ') : '(palette empty — keep briefs generic)'}
- Each level must feel distinct (new combination or twist, not a rehash).`;
  const user = `Game design:\n${dump}\n\nDesigner's request: ${brief.trim() || '(none — pitch the arc this game needs)'}\n\nPitch the campaign now. Output ONLY the JSON.`;
  const resp = await aiChat(client, {
    gameId,
    system,
    messages: [{ role: 'user', content: user }],
    maxTokens: 4096,
  });
  return parseAiJson(resp.text, CampaignPitchSchema);
}

/**
 * Write the pitch's NEW mechanics into the Content section as modular items
 * (## Name / _description_ / details). Skips names already present. Returns count added.
 */
export async function applyNewMechanics(
  client: CrucibleClient,
  args: {
    gameId: string;
    project: DesignProject;
    pitch: LevelPitch;
  },
): Promise<number> {
  const { gameId, project, pitch } = args;
  const cs = contentSection(project);
  if (!cs) return 0;
  const md = await readSection(client, gameId, cs.filename);
  const parsed = parseItems(md);
  const existing = new Set<string>();
  for (const item of parsed.items) {
    existing.add(item.name.trim().toLowerCase());
    for (const child of item.children) existing.add(child.name.trim().toLowerCase());
  }
  let added = 0;
  for (const m of pitch.mechanics) {
    if (m.status !== 'new') continue;
    if (existing.has(m.name.trim().toLowerCase())) continue;
    const item: DesignItem = {
      id:
        m.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'mechanic',
      name: m.name,
      slug: m.description,
      body: m.details ?? '',
      children: [],
    };
    parsed.items.push(item);
    existing.add(m.name.trim().toLowerCase());
    added += 1;
  }
  if (added > 0) await writeSection(client, gameId, cs.filename, serializeItems(parsed));
  return added;
}
